import { useState, createContext, useContext, useCallback } from "react";
import LargeToast from "../shared-components/LargeToast";
import PushToast from "../shared-components/PushToast";

type LargeToastStateType = {
  icon?: JSX.Element;
  title?: string;
  message?: string;
  isVisible: boolean;
  position?: string;
};

type PushToastInput = {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  onCancel?: () => void;
  type?:
    | "success"
    | "success-white-bg"
    | "notification"
    | "warning"
    | "error"
    | "info"
    | "neutral";
  autoDismiss?: boolean;
  duration?: number;
  /**
   * When true, non-sticky toasts cannot displace this one. Another sticky
   * toast still replaces it and invokes onCancel (used for chained Undo).
   */
  sticky?: boolean;
};

type PushToastStateType = PushToastInput & {
  isVisible: boolean;
};

type ApplicationToastContextType = {
  showLargeToast: (inputState: {
    title: string;
    message: string;
    icon?: JSX.Element;
    timeout?: number;
    position?: string;
  }) => void;
  hideLargeToast: () => void;
  showPushToast: (inputState: PushToastInput) => void;
  hidePushToast: () => void;
} | null;

const ApplicationToastContext =
  createContext<ApplicationToastContextType>(null);
const hideTimeout = 3000;

export const ApplicationToastProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const [largeToastState, setLargeToastState] = useState<LargeToastStateType>({
    isVisible: false,
  });

  const [pushToastState, setPushToastState] = useState<PushToastStateType>({
    message: "",
    actionLabel: "",
    onAction: () => {},
    isVisible: false,
  });

  const showLargeToast = useCallback(
    (inputState: {
      title: string;
      message: string;
      icon?: JSX.Element;
      timeout?: number;
      position?: string;
    }) => {
      setLargeToastState({ ...inputState, isVisible: true });
      setTimeout(
        () => {
          setLargeToastState({ ...inputState, isVisible: false });
        },
        inputState.timeout ? inputState.timeout : hideTimeout
      );
    },
    []
  );

  const hideLargeToast = useCallback(() => {
    setLargeToastState({ ...largeToastState, isVisible: false });
  }, [largeToastState]);

  const hidePushToast = useCallback(() => {
    setPushToastState((prev) => ({ ...prev, isVisible: false }));
  }, []);

  const showPushToast = useCallback((inputState: PushToastInput) => {
    setPushToastState((prev) => {
      if (prev.isVisible) {
        // Keep sticky toasts (e.g. Undo) visible when an unrelated toast tries
        // to replace them; otherwise onCancel would fire and commit early.
        if (prev.sticky && !inputState.sticky) {
          return prev;
        }
        prev.onCancel?.();
      }
      return { ...inputState, isVisible: true };
    });
  }, []);

  return (
    <ApplicationToastContext.Provider
      value={{
        showLargeToast,
        hideLargeToast,
        showPushToast,
        hidePushToast,
      }}
    >
      {children}
      <LargeToast state={largeToastState} onHide={hideLargeToast} />
      <PushToast state={pushToastState} onHide={hidePushToast} />
    </ApplicationToastContext.Provider>
  );
};

export function useApplicationToast() {
  const props = useContext(
    ApplicationToastContext
  ) as ApplicationToastContextType;

  return {
    showLargeToast: props?.showLargeToast,
    hideLargeToast: props?.hideLargeToast,
    showPushToast: props?.showPushToast,
    hidePushToast: props?.hidePushToast,
  };
}
